import { cloneDeep } from 'lodash-es';
import { SurveyDefinition, SurveyStepDefinition, WebViewStepDefinition, WebViewStepTemplate } from '../../../types/Survey';
import { ConsentSignatureResultModel, QuestionResultModel, SurveyResultModel, WebViewStepResultModel } from '../../../types/SurveyResult';

/** QuestionResultModel augmented with legacy `Answer` and `Choices` fields for use in WebViewStep iframe content. */
export interface LegacyQuestionResultModel extends QuestionResultModel {
    Answer: string;
    Choices: string[];
}

export interface WebViewSurveyAnswer {
    date?: string;
    stepIdentifier: string;
    resultIdentifier: string;
    answers: string[];
    // ConsentReviewStep only
    consented?: boolean;
    givenName?: string;
    familyName?: string;
}

/**
 * Clones the task result and adds legacy `Answer` (first answer) and `Choices`
 * (all answers) fields to every QuestionResult, matching the Angular implementation
 * used when injecting taskResult into fullHtml / partialHtml WebViewStep content.
 */
export function populateLegacyAnswerAndChoicesForQuestionSteps(taskResult: SurveyResultModel, surveyDefinition: SurveyDefinition): SurveyResultModel {
    if (!taskResult?.StepResults) return taskResult;

    const cloned: SurveyResultModel = cloneDeep(taskResult);

    for (const stepResult of cloned.StepResults) {
        var step = surveyDefinition.steps.find( function (s) {
            return s.identifier === stepResult.Identifier;
        });

        if (!stepResult.QuestionResults || !step) continue;

        for (const questionResult of stepResult.QuestionResults) {
            if (!questionResult || questionResult.Type !== 'QuestionResult') continue;

            const qr = questionResult as LegacyQuestionResultModel;
            const answers = (qr as QuestionResultModel).Answers ?? [];
            qr.Answer = answers[0] ?? '';
            qr.Choices = answers;
        }
    }

    return cloned;
}


export function buildCurrentSurveyAnswers(
    taskResult: SurveyResultModel,
    steps: SurveyStepDefinition[],
): WebViewSurveyAnswer[] {
    const validStepTypes = ['QuestionStep', 'FormStep', 'WebViewStep', 'ConsentReviewStep'];
    const surveyAnswers: WebViewSurveyAnswer[] = [];

    for (const stepResult of taskResult.StepResults) {
        const step = steps.find(
            s => s.identifier === stepResult.Identifier && validStepTypes.includes(s.type),
        );
        if (!step) continue;

        for (const questionResult of stepResult.QuestionResults) {
            const surveyAnswer: WebViewSurveyAnswer = {
                date: questionResult.EndDate,
                stepIdentifier: stepResult.Identifier,
                resultIdentifier: questionResult.Identifier,
                answers: [],
            };

            if (step.type === 'ConsentReviewStep') {
                const consentResult = questionResult as ConsentSignatureResultModel;
                surveyAnswer.consented = consentResult.Consented;
                surveyAnswer.givenName = consentResult.Signature?.GivenName;
                surveyAnswer.familyName = consentResult.Signature?.FamilyName;
            }

            const typedResult = questionResult as QuestionResultModel | WebViewStepResultModel;
            if ('Answers' in typedResult && typedResult.Answers) {
                surveyAnswer.answers.push(...typedResult.Answers);
            }
            if ('Result' in typedResult && typedResult.Result) {
                surveyAnswer.answers.push(typedResult.Result);
            }

            surveyAnswers.push(surveyAnswer);
        }
    }

    return surveyAnswers;
}

function escapeHtml(html: string): string {
    const text = document.createTextNode(html);
    const p = document.createElement('p');
    p.appendChild(text);
    return p.innerHTML;
}

export function getStepHtml(
    step: WebViewStepDefinition,
    webViewStepTemplates: WebViewStepTemplate[] | undefined,
): string {
    if (step.templateIdentifier) {
        const stepTemplate = webViewStepTemplates?.find(t => t.identifier === step.templateIdentifier);
        if (!stepTemplate) return '';
        let html = stepTemplate.html;
        for (const field of stepTemplate.fields) {
            const fieldValue = step.fieldValues?.[field.identifier];
            let value = fieldValue || field.defaultValue || '';
            if (field.editorType !== 'html') {
                value = escapeHtml(value);
            }
            html = html.replace(new RegExp('<%\\s*TemplateField\\.' + field.identifier + '\\s*%>', 'g'), value);
        }
        return html;
    }
    return step.html ?? '';
}

export function acceptableEventSource(
    event: MessageEvent,
    frameUrl: string | undefined | null,
    contentWindow: Window | null | undefined,
): boolean {
    if (!frameUrl) return false;
    const frameUrlParsed = new URL(frameUrl);
    const originUrl = new URL(event.origin);
    if (originUrl.hostname !== frameUrlParsed.hostname) return false;
    return !!contentWindow && event.source === contentWindow;
}